import { useState, useEffect, useCallback, useRef } from 'react';
import { adminApi, PO_NUMBER_MAX_LENGTH } from '../lib/api';
import { attemptKeyFor } from '../lib/attemptKey';

// ── Staff order entry ──
//
// Place an order on behalf of a customer (docs/admin-order-entry.md), for
// phone/email orders that used to be keyed straight into SOS. Rendered in
// place of the Customers tab, same swap pattern as OrderGuideEditor.
//
// Prices shown are a dry-run quote from the same server code that prices the
// real order (tier + specials), so what staff read out on the phone is
// exactly what SOS will say. The order still flows into SOS underneath.

const fmt$ = n => `$${(Number(n) || 0).toFixed(2)}`;

export default function AdminOrderEntry({ customer, onBack }) {
  const [catalog, setCatalog] = useState([]);
  const [usualItems, setUsualItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');

  const [search, setSearch] = useState('');
  const [lines, setLines] = useState([]);
  const [poNumber, setPoNumber] = useState('');
  const [requestedDate, setRequestedDate] = useState('');
  const [deliveryMethod, setDeliveryMethod] = useState('route');
  const [notes, setNotes] = useState('');
  const [emailCustomer, setEmailCustomer] = useState(true);
  const [addresses, setAddresses] = useState(null);
  const [addressId, setAddressId] = useState('');

  const [quote, setQuote] = useState(null);
  const [quoting, setQuoting] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  const quoteTimer = useRef(null);
  const attemptRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setLoadError('');
      try {
        const [cat, recent] = await Promise.all([
          adminApi.getCatalog(),
          adminApi.getCustomerOrders(customer.id, { pagesize: 20 }),
        ]);
        if (cancelled) return;
        setCatalog((cat.products || []).filter(p => p.status === 'visible' && p.id));
        // "Their usual" = items aggregated from recent orders, most-frequent
        // first — the phone-order fast path.
        const byItem = {};
        for (const o of (recent.orders || [])) {
          for (const l of (o.lines || [])) {
            if (!l.itemId) continue;
            const key = String(l.itemId);
            // SOS item names carry the full colon-delimited category path
            // ("Coffee:illy:Ground:illy Classico…") — show just the product.
            const rawName = l.itemName || l.description || `Item ${key}`;
            if (!byItem[key]) byItem[key] = { itemId: key, name: String(rawName).split(':').pop().trim() || rawName, sku: l.sku || '', description: l.description || '', count: 0, lastQty: l.quantity || 1 };
            byItem[key].count++;
          }
        }
        setUsualItems(Object.values(byItem).sort((a, b) => b.count - a.count).slice(0, 12));
      } catch (e) {
        if (!cancelled) setLoadError(e.message || 'Failed to load');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [customer.id]);

  // Ship-To choices, the same address book the customer picks from at
  // checkout. If it fails to load, the order still goes to their default.
  useEffect(() => {
    let cancelled = false;
    adminApi.getCustomerAddresses(customer.id)
      .then((r) => {
        if (cancelled) return;
        const list = r.addresses || [];
        setAddresses(list);
        setAddressId((list.find(a => a.isDefault) || list[0] || {}).id || '');
      })
      .catch(() => { if (!cancelled) setAddresses([]); });
    return () => { cancelled = true; };
  }, [customer.id]);

  const apiLines = useCallback((ls) => ls.map(l => ({
    itemId: String(l.itemId),
    quantity: l.quantity,
    description: l.description || l.name,
    price: l.listPrice || 0,
  })), []);

  // Re-quote (dry run) whenever the basket changes — debounced so rapid
  // quantity clicks don't hammer the API. The placeholder reference is fine:
  // dry runs create nothing.
  useEffect(() => {
    if (quoteTimer.current) clearTimeout(quoteTimer.current);
    if (!lines.length) { setQuote(null); return; }
    quoteTimer.current = setTimeout(async () => {
      setQuoting(true);
      try {
        const q = await adminApi.placeCustomerOrder(customer.id, {
          lines: apiLines(lines), poNumber: 'PRICE PREVIEW', dryRun: true,
        });
        setQuote(q);
        setError('');
      } catch (e) {
        setQuote(null);
        setError(e.message || 'Pricing preview failed');
      } finally {
        setQuoting(false);
      }
    }, 500);
    return () => { if (quoteTimer.current) clearTimeout(quoteTimer.current); };
  }, [lines, customer.id, apiLines]);

  function addItem(item, qty = 1) {
    setLines(prev => {
      const existing = prev.find(l => String(l.itemId) === String(item.itemId || item.id));
      if (existing) {
        return prev.map(l => l === existing ? { ...l, quantity: l.quantity + qty } : l);
      }
      return [...prev, {
        itemId: String(item.itemId || item.id),
        name: item.name,
        sku: item.sku || '',
        description: item.description || item.name,
        listPrice: item.price || 0,
        quantity: qty,
      }];
    });
  }

  function setQty(itemId, quantity) {
    const q = Math.max(0, parseInt(quantity, 10) || 0);
    setLines(prev => q === 0 ? prev.filter(l => l.itemId !== itemId) : prev.map(l => l.itemId === itemId ? { ...l, quantity: q } : l));
  }

  async function submit() {
    if (!lines.length) { setError('Add at least one item.'); return; }
    if (!poNumber.trim()) { setError('A PO number, reference, or email is required — for phone orders, note who called and when.'); return; }
    setSubmitting(true);
    setError('');
    try {
      const order = {
        lines: apiLines(lines),
        poNumber: poNumber.trim(),
        requestedDate: requestedDate || undefined,
        deliveryMethod,
        selectedAddressId: addressId || undefined,
        notes: notes.trim() || undefined,
        emailCustomer,
      };
      const r = await adminApi.placeCustomerOrder(customer.id, { ...order, submissionKey: attemptKeyFor(attemptRef, order) });
      attemptRef.current = null;
      setResult(r);
    } catch (e) {
      setError(e.message || 'Order failed');
    } finally {
      setSubmitting(false);
    }
  }

  const shipTo = (addresses || []).find(a => a.id === addressId) || null;
  const priceFor = (itemId) => quote?.pricedLines?.find(p => String(p.itemId) === String(itemId));
  const matches = search.trim().length >= 2
    ? catalog.filter(p => `${p.name} ${p.sku} ${p.brand || ''}`.toLowerCase().includes(search.trim().toLowerCase())).slice(0, 8)
    : [];

  if (result) {
    return (
      <div>
        <button style={st.back} onClick={onBack}>← Back to customers</button>
        <div style={st.successCard}>
          <h2 style={{ margin: '0 0 0.5rem', color: '#2e7d32' }}>Order {result.orderNumber} placed ✓</h2>
          <p style={{ margin: '0 0 1rem', color: '#555' }}>
            {result.alreadyPlaced ? result.message : (
              <>
                {customer.name} — {fmt$(result.total)}
                {result.emailedTo ? ` · confirmation emailed to ${result.emailedTo}` : ' · no confirmation email sent'}
              </>
            )}
          </p>
          <table style={st.table}>
            <tbody>
              {(result.pricedLines || []).map(l => (
                <tr key={l.itemId}>
                  <td style={st.td}>{l.description}</td>
                  <td style={st.tdR}>{l.quantity} × {fmt$(l.price)}{l.onSale ? ' (special)' : ''}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <button style={st.btnPrimary} onClick={() => { setLines([]); setPoNumber(''); setNotes(''); setQuote(null); setResult(null); }}>
            Place another order for {customer.name}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div>
      <button style={st.back} onClick={onBack}>← Back to customers</button>
      <h2 style={{ margin: '0 0 0.25rem' }}>New order — {customer.name}</h2>
      <p style={{ margin: '0 0 1rem', color: '#888', fontSize: '0.85rem' }}>
        Placed on the customer's behalf with their regular pricing. The order goes into SOS like any portal order.
      </p>

      {loading && <p style={{ color: '#888' }}>Loading catalog and order history…</p>}
      {loadError && <p style={st.errorText}>{loadError}</p>}

      {!loading && !loadError && (
        <div style={st.columns}>
          <div style={st.colLeft}>
            <input
              style={st.input}
              placeholder="Search catalog by name, SKU, or brand…"
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
            {matches.map(p => (
              <div key={p.id} style={st.pickRow} onClick={() => { addItem(p); setSearch(''); }}>
                <div>
                  <div style={{ fontWeight: 600 }}>{p.name}</div>
                  <div style={st.meta}>{p.sku}{p.brand ? ` · ${p.brand}` : ''} · list {fmt$(p.price)}</div>
                </div>
                <span style={st.addBtn}>+ Add</span>
              </div>
            ))}

            {usualItems.length > 0 && (
              <>
                <h4 style={st.sectionTitle}>Their usual</h4>
                {usualItems.map(u => (
                  <div key={u.itemId} style={st.pickRow} onClick={() => addItem(u, u.lastQty)}>
                    <div>
                      <div style={{ fontWeight: 600 }}>{u.name}</div>
                      <div style={st.meta}>{u.sku}{u.count > 1 ? ` · in ${u.count} of their recent orders` : ''}</div>
                    </div>
                    <span style={st.addBtn}>+ Add {u.lastQty > 1 ? `×${u.lastQty}` : ''}</span>
                  </div>
                ))}
              </>
            )}
          </div>

          <div style={st.colRight}>
            <h4 style={{ ...st.sectionTitle, marginTop: 0 }}>Order</h4>
            {lines.length === 0 && <p style={{ color: '#888', fontSize: '0.9rem' }}>No items yet — search or pick from their usual.</p>}
            {lines.map(l => {
              const p = priceFor(l.itemId);
              return (
                <div key={l.itemId} style={st.lineRow}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{l.name}</div>
                    <div style={st.meta}>
                      {p ? <>{fmt$(p.price)} each{p.onSale ? ' · special applied' : ''}</> : (quoting ? 'pricing…' : `list ${fmt$(l.listPrice)}`)}
                    </div>
                  </div>
                  <input
                    style={st.qtyInput}
                    type="number" min="0" value={l.quantity}
                    onChange={e => setQty(l.itemId, e.target.value)}
                  />
                  <button style={st.removeBtn} onClick={() => setQty(l.itemId, 0)}>×</button>
                </div>
              );
            })}

            {lines.length > 0 && (
              <div style={st.totalRow}>
                <span>Total{quoting ? ' (updating…)' : ''}</span>
                <b>{quote ? fmt$(quote.total) : '—'}</b>
              </div>
            )}

            <label style={st.label}>
              PO / reference <span style={{ color: '#A50B18' }}>*</span>
              <span style={{ float: 'right', color: '#777' }}>{poNumber.length}/{PO_NUMBER_MAX_LENGTH}</span>
            </label>
            <input style={st.input} value={poNumber} onChange={e => setPoNumber(e.target.value)}
              maxLength={PO_NUMBER_MAX_LENGTH}
              placeholder={`e.g. "Phone ${new Date().toLocaleDateString('en-US', { month: 'numeric', day: 'numeric' })} — Dana"`} />

            <div style={{ display: 'flex', gap: '0.75rem' }}>
              <div style={{ flex: 1 }}>
                <label style={st.label}>Requested delivery</label>
                <input style={st.input} type="date" value={requestedDate} onChange={e => setRequestedDate(e.target.value)} />
              </div>
              <div style={{ flex: 1 }}>
                <label style={st.label}>Delivery</label>
                <select style={st.input} value={deliveryMethod} onChange={e => setDeliveryMethod(e.target.value)}>
                  <option value="route">Route delivery</option>
                  <option value="pickup">Pickup</option>
                  <option value="fedex">FedEx</option>
                </select>
              </div>
            </div>

            <label style={st.label}>Ship to</label>
            {addresses === null ? (
              <p style={st.meta}>Loading addresses…</p>
            ) : addresses.length === 0 ? (
              <p style={{ ...st.meta, marginBottom: '0.75rem' }}>No Ship-To on file for this customer. Add one in SOS, or put the address in the notes.</p>
            ) : (
              <>
                <select style={{ ...st.input, marginBottom: '0.25rem' }} value={addressId} onChange={e => setAddressId(e.target.value)}>
                  {addresses.map(a => (
                    <option key={a.id} value={a.id}>{a.label}{a.isDefault ? ' — Default' : ''}</option>
                  ))}
                </select>
                {shipTo && (
                  <p style={{ ...st.meta, margin: '0 0 0.75rem' }}>
                    {[shipTo.line1, shipTo.line2, `${[shipTo.city, shipTo.state].filter(Boolean).join(', ')} ${shipTo.zip || ''}`.trim()].filter(Boolean).join(' · ')}
                  </p>
                )}
              </>
            )}

            <label style={st.label}>Notes for the team</label>
            <input style={st.input} value={notes} onChange={e => setNotes(e.target.value)} placeholder="Optional" />

            <label style={{ ...st.label, display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
              <input type="checkbox" checked={emailCustomer} onChange={e => setEmailCustomer(e.target.checked)} />
              Email the customer a confirmation
            </label>

            {error && <p style={st.errorText}>{error}</p>}
            <button style={{ ...st.btnPrimary, width: '100%', opacity: submitting ? 0.6 : 1 }} disabled={submitting} onClick={submit}>
              {submitting ? 'Placing order…' : `Place order for ${customer.name}`}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

const st = {
  back: { background: 'none', border: 'none', color: '#A50B18', cursor: 'pointer', padding: 0, marginBottom: '1rem', fontSize: '0.9rem' },
  columns: { display: 'flex', gap: '1.5rem', alignItems: 'flex-start', flexWrap: 'wrap' },
  colLeft: { flex: '1 1 340px', minWidth: 300 },
  colRight: { flex: '1 1 380px', minWidth: 320, background: '#fafafa', border: '1px solid #eee', borderRadius: 8, padding: '1rem' },
  sectionTitle: { margin: '1.25rem 0 0.5rem', fontSize: '0.85rem', textTransform: 'uppercase', letterSpacing: '0.05em', color: '#888' },
  input: { width: '100%', padding: '0.55rem 0.7rem', border: '1px solid #ddd', borderRadius: 6, fontSize: '0.95rem', boxSizing: 'border-box', marginBottom: '0.75rem', background: '#fff' },
  pickRow: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0.5rem 0.6rem', border: '1px solid #eee', borderRadius: 6, marginBottom: '0.4rem', cursor: 'pointer', background: '#fff' },
  meta: { fontSize: '0.78rem', color: '#888' },
  addBtn: { color: '#A50B18', fontWeight: 600, fontSize: '0.85rem', whiteSpace: 'nowrap', marginLeft: '0.75rem' },
  lineRow: { display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.45rem 0', borderBottom: '1px solid #eee' },
  qtyInput: { width: 64, padding: '0.4rem', border: '1px solid #ddd', borderRadius: 6, textAlign: 'center' },
  removeBtn: { background: 'none', border: 'none', color: '#c0392b', fontSize: '1.1rem', cursor: 'pointer', padding: '0 0.25rem' },
  totalRow: { display: 'flex', justifyContent: 'space-between', padding: '0.75rem 0', fontSize: '1.05rem', borderBottom: '2px solid #ddd', marginBottom: '1rem' },
  label: { display: 'block', fontSize: '0.8rem', color: '#555', margin: '0.25rem 0 0.25rem' },
  btnPrimary: { background: '#A50B18', color: '#fff', border: 'none', borderRadius: 6, padding: '0.7rem 1.2rem', fontSize: '0.95rem', fontWeight: 600, cursor: 'pointer' },
  errorText: { color: '#c0392b', fontSize: '0.85rem' },
  successCard: { background: '#f5faf5', border: '1px solid #cde8cd', borderRadius: 8, padding: '1.25rem', maxWidth: 560 },
  table: { borderCollapse: 'collapse', width: '100%', marginBottom: '1rem' },
  td: { padding: '0.3rem 0', fontSize: '0.9rem' },
  tdR: { padding: '0.3rem 0', fontSize: '0.9rem', textAlign: 'right', whiteSpace: 'nowrap' },
};
